import {Injectable} from '@angular/core';

import {ConfigService} from '@ngmedax/config';
import * as pako from 'pako';

import {configKeys} from '../patient.config-keys';


declare const window: any;

/**
 * Service for generating mobile frontend links with encoding and compression.
 * Handles link generation, pair codes, and mail client integration.
 */
@Injectable()
export class MobileLinkService {
  /**
   * Injects dependencies.
   */
  public constructor(
    private configService: ConfigService
  ) {
  }

  /**
   * Builds the mobile frontend link for a patient.
   *
   * @param patientUid The patient UID.
   * @param locale The locale string.
   * @param encodeUri Whether to encode the URI (default: false).
   * @returns The mobile frontend link.
   */
  public buildMobileFrontendLink(patientUid: string, locale: string, encodeUri: boolean = false): string {
    const mobileApiUri = this.configService.get(configKeys.MOBILE_URI_CONFIG_KEY);
    const mobileFrontendUri = this.configService.get(configKeys.MOBILE_FRONTEND_URI_CONFIG_KEY) || '/app';

    const pairCode = this.buildPairCode(mobileApiUri);
    const assignCode = this.buildAssignCode(patientUid);

    let baseUrl = mobileFrontendUri.match(/\/$/) ? mobileFrontendUri : mobileFrontendUri + '/';

    // add protocol and host if not present
    if (!baseUrl.match(/^htt/)) {
      baseUrl = `${window.location.protocol}//${window.location.host}${baseUrl}`;
    }

    const queryString = `autoPairMode=true&pairCode=${pairCode}&assignCode=${assignCode}&locale=${locale}`;

    const encode = encodeUri ? encodeURIComponent : (u: string) => u;

    // try to compress and encode the query string
    if (window && window.btoa) {
      try {
        const enc = this.xorEncode(queryString, 23);
        const gzipped = pako.deflate(enc, {to: 'string'});
        const base64 = window.btoa(gzipped);
        return encode(`${baseUrl}?c=${base64}`);
      } catch (error) {
        // compression failed, fall back to plain query string
      }
    }

    return encode(`${baseUrl}?${queryString}`);
  }

  /**
   * Builds the pair code for mobile app pairing.
   *
   * @param mobileApiUri The mobile API URI.
   * @returns The pair code string.
   */
  public buildPairCode(mobileApiUri: string): string {
    return `mymedax|serverurl|${mobileApiUri}`;
  }

  /**
   * Builds the assign code for patient assignment.
   *
   * @param patientUid The patient UID.
   * @returns The assign code string.
   */
  public buildAssignCode(patientUid: string): string {
    return `mymedax|patient|${patientUid}`;
  }

  /**
   * Builds a QR code data string for the mobile app.
   *
   * @param patientUid The patient UID.
   * @param locale The locale string.
   * @returns The QR code data string.
   */
  public buildQrCodeData(patientUid: string, locale: string): string {
    return `mymedax|patient|${patientUid}&locale=${locale}`;
  }

  /**
   * Opens the default mail client with a link in the body.
   * Uses a temporary anchor element to avoid redirecting the current page.
   *
   * @param email The recipient email (optional).
   * @param bodyText The email body text.
   */
  public openMailClient(email: string, bodyText: string): void {
    const toPart = email || '';
    const mailtoUrl = `mailto:${toPart}?body=${encodeURIComponent(bodyText)}`;

    // create temporary anchor to open mailto in new window/tab
    const anchor = document.createElement('a');
    anchor.href = mailtoUrl;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }

  /**
   * Opens the mobile frontend link in a new browser window.
   *
   * @param link The link to open.
   */
  public openInNewWindow(link: string): void {
    if (window) {
      window.open(link, '_blank');
    }
  }

  /**
   * XOR encodes a string with the given key.
   *
   * @param str The string to encode.
   * @param key The XOR key.
   * @returns The encoded string.
   */
  private xorEncode(str: string, key: number): string {
    return str.split('').reduce((all, char) => all += String.fromCharCode(key ^ char.charCodeAt(0)), '');
  }
}

